/**
 * @fileoverview Input/output schemas for the character-image-creator-fal-v2 agent.
 *
 * This agent generates portrait images for characters using the fal.ai provider.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.imagePrompt.schema)
  .extend({
    referenceImageUris: z.array(z.string()).optional().describe('An array of Base64 encoded image data URIs to use as reference.'),
    overrideModel: z.string().optional().describe("An optional model identifier to override the agent's default."),
    // Legacy param - kept for backward compatibility
    enableSafetyChecker: z.boolean().optional().default(false).describe('Enable or disable the FAL safety checker. Defaults to false.'),
    // New synthetic params for generalized image generation
    aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('Normalized aspect ratio. Maps to model-specific formats.'),
    quality: z.enum(['draft', 'standard', 'high']).optional().describe('Quality level. Maps to resolution or inference steps depending on model.'),
    numberOfImages: z.number().min(1).max(4).optional().describe('Number of images to generate (1-4).'),
    outputFormat: z.enum(['png', 'jpeg', 'webp']).optional().describe('Output image format.'),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  imageDataUri: z.string().describe('The generated image, returned as a Base64-encoded data URI.'),
  seed: z.number().optional().describe('The seed used for this generation, if returned by the model.'),
  generatingModel: z.string().optional().describe('The FAL model identifier used for generation.'),
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('The normalized aspect ratio used.'),
  quality: z.enum(['draft', 'standard', 'high']).optional().describe('The quality level used.'),
  safetyChecker: z.boolean().optional().describe('Whether safety checker was enabled.'),
  timings: z.object({ inference: z.number().optional(), total: z.number().optional() }).optional().describe('Generation timing data in ms.'),
});

export type Output = z.infer<typeof OutputSchema>;
